/**
 * Comprehensive Seat Layout Configurations
 * Supports: 11, 14, 16, 25, 33, and 65 seater vehicles
 * 
 * Each configuration defines:
 * - Layout structure (rows, columns, gaps)
 * - Driver position
 * - Seat arrangement patterns
 * - Visual styling properties
 */

export const SEAT_CONFIGS = {
  // 11-Seater Configuration (Van/Matatu)
  11: {
    name: '11-Seater Van',
    layout: {
      type: 'asymmetric',
      driverPosition: 'front-right',
      structure: [
        { section: 'driver', seats: 1, position: 'front-right' },
        { section: 'left', seats: 6, pattern: '1-per-row' },
        { section: 'right', seats: 4, pattern: '1-per-row' }
      ]
    },
    visual: {
      containerWidth: '300px',
      seatSize: '40px',
      gapBetweenColumns: '48px',
      gapBetweenRows: '16px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 1, totalRows: 6, startIndex: 0 },
      rightColumn: { seatsPerRow: 1, totalRows: 5, startIndex: 6 }
    }
  },

  // 14-Seater Configuration (Standard Matatu)
  14: {
    name: '14-Seater Matatu',
    layout: {
      type: 'asymmetric',
      driverPosition: 'front-right',
      structure: [
        { section: 'driver', seats: 1, position: 'front-right' },
        { section: 'left', seats: 10, pattern: '2-per-row' },
        { section: 'right', seats: 4, pattern: '1-per-row' }
      ]
    },
    visual: {
      containerWidth: '350px',
      seatSize: '40px',
      gapBetweenColumns: '48px',
      gapBetweenRows: '16px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 2, totalRows: 5, startIndex: 0 },
      rightColumn: { seatsPerRow: 1, totalRows: 4, startIndex: 10 }
    }
  },

  // 16-Seater Configuration (Extended Matatu)
  16: {
    name: '16-Seater Extended Matatu',
    layout: {
      type: 'asymmetric',
      driverPosition: 'front-right',
      structure: [
        { section: 'driver', seats: 1, position: 'front-right' },
        { section: 'left', seats: 10, pattern: '2-per-row' },
        { section: 'right', seats: 6, pattern: '1-per-row' }
      ]
    },
    visual: {
      containerWidth: '350px',
      seatSize: '40px',
      gapBetweenColumns: '48px',
      gapBetweenRows: '16px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 2, totalRows: 5, startIndex: 0 },
      rightColumn: { seatsPerRow: 1, totalRows: 6, startIndex: 10 }
    }
  },

  // 25-Seater Configuration (Minibus)
  25: {
    name: '25-Seater Minibus',
    layout: {
      type: 'symmetric-with-back',
      driverPosition: 'front-left',
      structure: [
        { section: 'driver', seats: 1, position: 'front-left' },
        { section: 'main-left', seats: 12, pattern: '2-per-row' },
        { section: 'main-right', seats: 11, pattern: '2-per-row' },
        { section: 'back-row', seats: 1, pattern: 'center' }
      ]
    },
    visual: {
      containerWidth: '400px',
      seatSize: '40px',
      gapBetweenColumns: '60px',
      gapBetweenRows: '16px',
      aisleWidth: '40px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 2, totalRows: 6, startIndex: 0 },
      rightColumn: { seatsPerRow: 2, totalRows: 6, startIndex: 12 },
      backRow: { seats: [25], position: 'center' }
    }
  },

  // 33-Seater Configuration (Standard Bus)
  33: {
    name: '33-Seater Bus',
    layout: {
      type: 'symmetric-with-back',
      driverPosition: 'front-left',
      structure: [
        { section: 'driver', seats: 1, position: 'front-left' },
        { section: 'main-left', seats: 16, pattern: '2-per-row' },
        { section: 'main-right', seats: 16, pattern: '3-per-row' },
        { section: 'back-row', seats: 1, pattern: 'center' }
      ]
    },
    visual: {
      containerWidth: '450px',
      seatSize: '40px',
      gapBetweenColumns: '60px',
      gapBetweenRows: '16px',
      aisleWidth: '40px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 2, totalRows: 7, startIndex: 0 },
      rightColumn: { seatsPerRow: 3, totalRows: 6, startIndex: 14 },
      backRow: { seats: [33], position: 'center' }
    }
  },

  // 65-Seater Configuration (Large Bus/Coach)
  65: {
    name: '65-Seater Coach',
    layout: {
      type: 'symmetric-with-back',
      driverPosition: 'front-left',
      structure: [
        { section: 'driver', seats: 1, position: 'front-left' },
        { section: 'main-left', seats: 32, pattern: '2-per-row' },
        { section: 'main-right', seats: 32, pattern: '2-per-row' },
        { section: 'back-row', seats: 1, pattern: 'center' }
      ]
    },
    visual: {
      containerWidth: '500px',
      seatSize: '32px',
      gapBetweenColumns: '70px',
      gapBetweenRows: '12px',
      aisleWidth: '50px'
    },
    seatArrangement: {
      leftColumn: { seatsPerRow: 2, totalRows: 16, startIndex: 0 },
      rightColumn: { seatsPerRow: 2, totalRows: 16, startIndex: 32 },
      backRow: { seats: [65], position: 'center' }
    }
  }
}

/**
 * Get seat configuration by capacity
 * @param {number} seatCount - Total number of seats
 * @returns {object} Configuration object
 */
export const getSeatConfig = (seatCount) => {
  const config = SEAT_CONFIGS[seatCount]
  if (!config) {
    console.warn(`No configuration found for ${seatCount} seats. Using default 14-seater config.`)
    return SEAT_CONFIGS[14]
  }
  return config
}

/**
 * Calculate seat positions for a given configuration
 * @param {number} seatCount - Total number of seats
 * @param {Array} seatData - Array of seat objects from API
 * @returns {object} Organized seat layout with positions
 */
export const calculateSeatLayout = (seatCount, seatData) => {
  const config = getSeatConfig(seatCount)
  const { seatArrangement } = config
  
  const layout = {
    driver: null,
    leftColumn: [],
    rightColumn: [],
    backRow: [],
    config
  }

  // Sort seats by seat number
  const sortedSeats = [...seatData].sort((a, b) => a.id - b.id)

  // Arrange seats according to configuration
  if (seatArrangement.leftColumn) {
    const { seatsPerRow, totalRows, startIndex } = seatArrangement.leftColumn
    
    for (let row = 0; row < totalRows; row++) {
      const rowSeats = []
      for (let seat = 0; seat < seatsPerRow; seat++) {
        const seatIndex = startIndex + (row * seatsPerRow) + seat
        if (sortedSeats[seatIndex]) {
          rowSeats.push(sortedSeats[seatIndex])
        }
      }
      if (rowSeats.length > 0) {
        layout.leftColumn.push(rowSeats)
      }
    }
  }

  if (seatArrangement.rightColumn) {
    const { seatsPerRow, totalRows, startIndex } = seatArrangement.rightColumn
    
    for (let row = 0; row < totalRows; row++) {
      const rowSeats = []
      for (let seat = 0; seat < seatsPerRow; seat++) {
        const seatIndex = startIndex + (row * seatsPerRow) + seat
        if (sortedSeats[seatIndex]) {
          rowSeats.push(sortedSeats[seatIndex])
        }
      }
      if (rowSeats.length > 0) {
        layout.rightColumn.push(rowSeats)
      }
    }
  }

  // Handle back row for configurations that have it
  if (seatArrangement.backRow) {
    const { seats } = seatArrangement.backRow
    layout.backRow = seats.map(seatIndex => 
      sortedSeats.find(seat => seat.id === seatIndex + 1)
    ).filter(Boolean)
  }

  return layout
}

/**
 * Validate if seat count is supported
 * @param {number} seatCount - Number of seats to validate
 * @returns {boolean} Whether the configuration is supported
 */
export const isSeatCountSupported = (seatCount) => {
  return Object.keys(SEAT_CONFIGS).includes(seatCount.toString())
}

/**
 * Get all supported seat counts
 * @returns {Array} Array of supported seat counts
 */
export const getSupportedSeatCounts = () => {
  return Object.keys(SEAT_CONFIGS).map(Number).sort((a, b) => a - b)
}

/**
 * Validate seat configuration calculations
 * @param {number} seatCount - Number of seats to validate
 * @returns {object} Validation result with details
 */
export const validateSeatConfig = (seatCount) => {
  const config = getSeatConfig(seatCount)
  const { seatArrangement } = config
  
  let calculatedTotal = 0
  let details = {}
  
  if (seatArrangement.leftColumn) {
    const leftTotal = seatArrangement.leftColumn.seatsPerRow * seatArrangement.leftColumn.totalRows
    calculatedTotal += leftTotal
    details.leftColumn = leftTotal
  }
  
  if (seatArrangement.rightColumn) {
    const rightTotal = seatArrangement.rightColumn.seatsPerRow * seatArrangement.rightColumn.totalRows
    calculatedTotal += rightTotal
    details.rightColumn = rightTotal
  }
  
  if (seatArrangement.backRow) {
    const backTotal = seatArrangement.backRow.seats.length
    calculatedTotal += backTotal
    details.backRow = backTotal
  }
  
  return {
    seatCount,
    calculatedTotal,
    isValid: calculatedTotal === seatCount,
    details,
    config: config.name
  }
}

/**
 * Validate all seat configurations
 * @returns {Array} Array of validation results
 */
export const validateAllConfigs = () => {
  return getSupportedSeatCounts().map(count => validateSeatConfig(count))
}